/**
 * Protected Route wrapper component with auth checks
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress } from '@mui/material';

/**
 * ProtectedRoute component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {string} props.requiredPermission - Optional permission required (format: "module:action")
 * @returns {React.ReactNode}
 */
export default function ProtectedRoute({ children, requiredPermission }) {
  const { isAuthenticated, loading, isAuthChecked, hasPermission } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading || !isAuthChecked) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permission if required
  if (requiredPermission) {
    const [module, action] = requiredPermission.split(':');
    if (!hasPermission(module, action)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
}
